import { Box, Flex, HStack, VStack } from '@televet/kibble-ui/build/chakra';
import { Button } from '@televet/kibble-ui/build/components/Button';
import { Icon } from '@televet/kibble-ui/build/components/Icon';
import { Text } from '@televet/kibble-ui/build/components/Text';
import { format } from 'date-fns';
import { updateAppointmentModal } from 'pages/Appointments/state/appointmentsModalSlice';
import React, { MouseEvent, useCallback, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { RouteBasePaths } from 'routes';
import { GraphQLFetchPolicies } from 'shared/enums';
import { useRecap } from 'shared/providers/RecapProvider/RecapProvider';
import { useGetNewAppointmentModalRecapsQuery, useSubscribeToAppointmentRecapSubscription } from 'shared/types/graphql';
import { useAppDispatch, useAppSelector } from 'state/hooks';

export const AiRecapTab = (): JSX.Element => {
  const dispatch = useAppDispatch();
  const history = useHistory();
  const { appointmentId } = useAppSelector((state) => state.appointmentsModal);
  const { isRecapActive, setRecapAppointmentId, toggleRecap } = useRecap();

  const { data, refetch } = useGetNewAppointmentModalRecapsQuery({
    variables: {
      appointmentId: appointmentId || '',
    },
    skip: !appointmentId,
  });

  const { appointmentRecaps, recapNotes } = useMemo(() => {
    return {
      appointmentRecaps: data?.findManyRecap || [],
      recapNotes: data?.findUniqueAppointment?.recapNotes || '',
    };
  }, [data]);

  useSubscribeToAppointmentRecapSubscription({
    variables: { where: { appointmentId: { equals: appointmentId } } },
    skip: !appointmentId,
    onSubscriptionData: () => {
      refetch();
    },
    fetchPolicy: GraphQLFetchPolicies.NetworkOnly,
  });

  const navigateToRecapNotes = useCallback((): void => {
    dispatch(
      updateAppointmentModal({
        isOpen: false,
      }),
    );
    history.push(`${RouteBasePaths.Appointments}/${appointmentId}/recap`);
  }, [appointmentId, dispatch, history]);

  const onRecapClick = useCallback(
    (e: MouseEvent<HTMLButtonElement>): void => {
      e.stopPropagation();
      if (!isRecapActive && appointmentId) {
        setRecapAppointmentId(appointmentId);
        toggleRecap();
      }
    },
    [appointmentId, isRecapActive, setRecapAppointmentId, toggleRecap],
  );

  const length = appointmentRecaps.length || 0;

  return (
    <VStack w="100%" spacing={4}>
      <VStack w="100%" backgroundColor="background.subtle" p={3} borderRadius="md" spacing={3} mt={4}>
        <Text as="p" noOfLines={3} isTruncated={true}>
          {recapNotes || 'No AI recap notes available.'}
        </Text>

        <Button variant="tertiary" w="100%" onClick={navigateToRecapNotes} disabled={!recapNotes}>
          <Text size="sm" fontWeight="bold">
            View Recap & Transcript
          </Text>
        </Button>
      </VStack>
      <VStack w="100%" spacing={4}>
        {appointmentRecaps && length > 0 ? (
          appointmentRecaps.map((recap, index) => {
            const status = !!recap.generatedText ? 'success' : 'warning';
            return (
              <Box key={recap.id} w="100%">
                <HStack mb={1} alignItems="center" justifyContent="space-between">
                  <Text size="sm">Recording #{length - index}</Text>
                  <Box bg={`background.${status}`} px={2} borderRadius="md" fontSize="sm">
                    <Text size="xs" fontWeight="semibold" color={`text.${status}`} lineHeight={1}>
                      {!!recap.generatedText ? 'Completed' : 'Processing'}
                    </Text>
                  </Box>
                </HStack>
                <VStack spacing={0} align="flex-start">
                  <Text variant="subtle" size="xs">
                    Created {format(new Date(recap.createdAt), "EEE, MMM d 'at' h:mm a")}
                  </Text>
                  <Flex alignItems="center" mt={1}>
                    <Icon name="person" size="xs" variant="subtle" />
                    <Text variant="subtle" size="xs" ml={1}>
                      {recap.createdBy.firstName} {recap.createdBy.lastName}
                    </Text>
                  </Flex>
                </VStack>
              </Box>
            );
          })
        ) : (
          <Text as="p" size="sm" variant="subtle">
            No recaps available.
          </Text>
        )}
      </VStack>
      <Button
        variant="ai"
        size="md"
        iconName="aiMicrophone"
        w="100%"
        onClick={onRecapClick}
        isDisabled={isRecapActive || !appointmentId}
      >
        New Recording
      </Button>
    </VStack>
  );
};
